//	Client-only helpers: rasterize the live SVG to PNG and copy the block anchor.

const STYLE_PROPS = [
	"fill", "fill-opacity", "stroke-dasharray", "stroke", "stroke-width", "stroke-opacity", "stroke-linecap", "stroke-linejoin",
	"opacity", "font-family", "font-size", "font-weight", "font-variant-numeric", "text-anchor",
	"dominant-baseline", "letter-spacing", "visibility", "paint-order", "text-transform",
];
const GENERIC = /^(serif|sans-serif|monospace|system-ui|-apple-system|BlinkMacSystemFont|Arial|Helvetica|Georgia|Times New Roman|Segoe UI|Roboto)$/i;
const FALLBACK_FONT = "Arial, Helvetica, sans-serif";

//	copy computed styles node by node so CSS custom properties and scoped classes resolve in the detached clone
function inlineStyles(source, clone) {
	const a = [source, ...source.querySelectorAll("*")];
	const b = [clone, ...clone.querySelectorAll("*")];
	const families = new Set();
	for (let i = 0; i < a.length && i < b.length; i++) {
		const cs = getComputedStyle(a[i]);
		for (const prop of STYLE_PROPS) {
			const v = cs.getPropertyValue(prop);
			if (v) b[i].style.setProperty(prop, v);
		}
		if (a[i].tagName === "text" || a[i].tagName === "tspan") families.add(cs.fontFamily.split(",")[0].trim().replace(/^"|"$/g, ""));
		//	transforms carry the hover lift; the export is the resting state
		b[i].style.removeProperty("transition");
	}
	return families;
}

async function fetchWithTimeout(url, ms) {
	const ctrl = typeof AbortController === "function" ? new AbortController() : null;
	const timer = setTimeout(() => ctrl && ctrl.abort(), ms);
	try {
		const res = await fetch(url, { signal: ctrl ? ctrl.signal : undefined, credentials: "same-origin" });
		if (!res.ok) throw new Error(String(res.status));
		return await res.blob();
	} finally {
		clearTimeout(timer);
	}
}

const toDataUrl = (blob) =>
	new Promise((resolve, reject) => {
		const r = new FileReader();
		r.onload = () => resolve(r.result);
		r.onerror = reject;
		r.readAsDataURL(blob);
	});

//	webfonts used by chart text, embedded as data: @font-face; empty when no custom face is needed, null when unreadable
async function embedFonts(families) {
	const wanted = [...families].filter((f) => f && !GENERIC.test(f));
	if (!wanted.length) return "";
	if (!document.fonts || typeof document.fonts.check !== "function") return null;
	let css = "";
	for (const family of wanted) {
		if (!document.fonts.check(`12px "${family}"`)) return null;
		let found = false;
		for (const sheet of document.styleSheets) {
			let rules;
			try {
				rules = sheet.cssRules;
			} catch (_e) {
				continue; // cross-origin stylesheet
			}
			for (const rule of rules) {
				if (!(rule instanceof CSSFontFaceRule)) continue;
				if (rule.style.getPropertyValue("font-family").replace(/["']/g, "").trim() !== family) continue;
				const src = rule.style.getPropertyValue("src");
				const m = /url\((["']?)([^"')]+)\1\)/.exec(src);
				if (!m) continue;
				try {
					const blob = await fetchWithTimeout(new URL(m[2], sheet.href || location.href).href, 1500);
					const data = await toDataUrl(blob);
					css += `@font-face{font-family:"${family}";font-weight:${rule.style.getPropertyValue("font-weight") || "normal"};font-style:${rule.style.getPropertyValue("font-style") || "normal"};src:url(${data});}`;
					found = true;
				} catch (_e) {
					/* unreadable face: fall back below */
				}
			}
		}
		if (!found) return null;
	}
	return css;
}

const slug = (s) =>
	(s || "grafic")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 60) || "grafic";

/**
 * svg: the live <svg>; chrome: { title, subtitle, source, legend: [{name, color}], background, font, titleFont }
 */
export async function exportPng({ svg, title, subtitle, source, legend = [], background = "#ffffff", font = FALLBACK_FONT, titleFont = "" }) {
	const vb = svg.viewBox.baseVal;
	const w = Math.round(vb && vb.width ? vb.width : svg.clientWidth);
	const h = Math.round(vb && vb.height ? vb.height : svg.clientHeight);
	const clone = svg.cloneNode(true);
	const families = inlineStyles(svg, clone);
	for (const node of clone.querySelectorAll('[data-export="skip"]')) node.remove();
	clone.removeAttribute("tabindex");
	clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
	clone.setAttribute("width", String(w));
	clone.setAttribute("height", String(h));
	const fontCss = await embedFonts(families);
	if (fontCss) {
		const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
		style.textContent = fontCss;
		clone.insertBefore(style, clone.firstChild);
	} else if (fontCss === null) {
		// Keep the declared native fallbacks (including Georgia/monospace). Canvas
		// chrome can use the document's fonts; only the detached SVG needs embedding.
		for (const t of clone.querySelectorAll("text, tspan")) {
			const native = t.style.fontFamily.split(",").filter((f) => GENERIC.test(f.trim().replace(/["']/g, ""))).join(",");
			t.style.fontFamily = native || FALLBACK_FONT;
		}
	}

	const xml = new XMLSerializer().serializeToString(clone);
	const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
	const img = new Image();
	img.decoding = "sync";
	try {
		await new Promise((resolve, reject) => {
			img.onload = resolve;
			img.onerror = () => reject(new Error("svg rasterization failed"));
			img.src = url;
		});
	} finally {
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}

	const pad = 24;
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("canvas unavailable");
	const titleStyle = `700 24px ${titleFont || font}`;
	const bodyStyle = `400 14px ${font}`;
	const metaStyle = `400 13px ${font}`;
	const linesFor = (text, style, maxWidth = w) => {
		ctx.font = style;
		return wrapText(text, maxWidth, (value) => ctx.measureText(value).width);
	};
	const titleLines = linesFor(title, titleStyle);
	const subtitleLines = linesFor(subtitle, bodyStyle);
	const sourceLines = linesFor(source ? `Sursa: ${source}` : "", metaStyle);
	const legendLayout = [];
	let legendX = 0, legendY = 0, rowH = 0;
	for (const it of legend) {
		const lines = linesFor(it.name, metaStyle, w - 34);
		const itemW = Math.min(w, Math.max(...lines.map((l) => ctx.measureText(l).width), 0) + 46);
		if (legendX && legendX + itemW > w) { legendY += rowH; legendX = 0; rowH = 0; }
		legendLayout.push({ ...it, lines, x: legendX, y: legendY });
		rowH = Math.max(rowH, lines.length * 20 + 8);
		legendX += itemW;
	}
	const legendH = legendY + rowH;
	const titleH = titleLines.length ? titleLines.length * 29 + 8 : 0;
	const subtitleH = subtitleLines.length ? subtitleLines.length * 21 + 12 : 0;
	const headerH = titleH + subtitleH + legendH;
	const totalH = h + headerH + (sourceLines.length ? sourceLines.length * 20 + 20 : 0) + 2 * pad;
	const scale = Math.min(2, 4096 / Math.max(w + 2 * pad, totalH));
	canvas.width = Math.round((w + 2 * pad) * scale);
	canvas.height = Math.round(totalH * scale);
	ctx.scale(scale, scale);
	ctx.fillStyle = background;
	ctx.fillRect(0, 0, w + 2 * pad, totalH);
	const ink = getComputedStyle(svg).getPropertyValue("--_ink") || "#111";
	const muted = getComputedStyle(svg).getPropertyValue("--_ink-muted") || "#666";
	ctx.textBaseline = "top";
	const drawLines = (lines, style, color, x, y, leading) => {
		ctx.font = style;
		ctx.fillStyle = color;
		lines.forEach((line, i) => ctx.fillText(line, x, y + i * leading));
	};
	drawLines(titleLines, titleStyle, ink, pad, pad, 29);
	drawLines(subtitleLines, bodyStyle, muted, pad, pad + titleH, 21);
	for (const it of legendLayout) {
		const x = pad + it.x, y = pad + titleH + subtitleH + it.y;
		ctx.fillStyle = it.color;
		if (it.kind === "line") {
			ctx.strokeStyle = it.color;
			ctx.lineWidth = 2.5;
			ctx.setLineDash(!it.dash || it.dash === "none" ? [] : it.dash.split(" ").map(Number));
			ctx.beginPath(); ctx.moveTo(x, y + 7); ctx.lineTo(x + 24, y + 7); ctx.stroke();
			ctx.setLineDash([]);
		} else ctx.fillRect(x, y + 3, 12, 10);
		drawLines(it.lines, metaStyle, ink, x + 32, y, 20);
	}
	ctx.drawImage(img, pad, pad + headerH, w, h);
	drawLines(sourceLines, metaStyle, muted, pad, pad + headerH + h + 20, 20);

	const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
	if (!blob) throw new Error("png encoding failed");
	const filename = `grafic-${slug(title)}.png`;
	const file = typeof File === "function" ? new File([blob], filename, { type: "image/png" }) : null;
	if (file && navigator.canShare && navigator.canShare({ files: [file] }) && /iPhone|iPad/.test(navigator.userAgent)) {
		try {
			await navigator.share({ files: [file], title: title || "Grafic" });
			return blob;
		} catch (_e) {
			/* dismissed: fall through to download */
		}
	}
	const a = document.createElement("a");
	a.href = URL.createObjectURL(blob);
	a.download = filename;
	a.rel = "noopener";
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(a.href), 5000);
	return blob;
}

export async function copyLink(id) {
	const url = new URL(location.href);
	url.hash = `chart-${id}`;
	const text = url.href;
	try {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch (_e) {
		/* permission denied: legacy path */
	}
	try {
		const ta = document.createElement("textarea");
		ta.value = text;
		ta.setAttribute("readonly", "");
		ta.style.position = "fixed";
		ta.style.opacity = "0";
		document.body.appendChild(ta);
		ta.select();
		const ok = document.execCommand("copy");
		ta.remove();
		return ok;
	} catch (_e) {
		return false;
	}
}

export function wrapText(text, maxWidth, measure) {
	if (!text) return [];
	const lines = [];
	let line = "";
	for (const word of String(text).trim().split(/\s+/)) {
		if (line && measure(`${line} ${word}`) <= maxWidth) { line += ` ${word}`; continue; }
		if (line) { lines.push(line); line = ""; }
		for (const char of word) {
			if (line && measure(line + char) > maxWidth) { lines.push(line); line = ""; }
			line += char;
		}
	}
	if (line) lines.push(line);
	return lines;
}
